/**
 * Unified interface for device capability detection (web and native)
 * React Native's platform-specific file resolution automatically uses:
 * - web.ts on web platforms
 * - web.native.ts on native platforms
 */

import { DevicePerformanceLevel as PlatformDevicePerformanceLevel } from './consts';
import { getDevicePerformanceLevel as getPlatformDevicePerformanceLevel } from './web';

// unified enum
export enum DevicePerformanceLevel {
  LOW = 'LOW',
  MEDIUM = 'MEDIUM',
  HIGH = 'HIGH',
}

function mapToUnifiedLevel(platformLevel: PlatformDevicePerformanceLevel): DevicePerformanceLevel {
  switch (platformLevel) {
    case PlatformDevicePerformanceLevel.LOW:
      return DevicePerformanceLevel.LOW;
    case PlatformDevicePerformanceLevel.MEDIUM:
      return DevicePerformanceLevel.MEDIUM;
    case PlatformDevicePerformanceLevel.HIGH:
      return DevicePerformanceLevel.HIGH;
    default:
      return DevicePerformanceLevel.MEDIUM;
  }
}

/**
 * Gets device performance level for web and native platforms
 *
 * Note: Native platforms currently return HIGH as default.
 * Web platforms support all three levels (LOW, MEDIUM, HIGH) based on
 * CPU cores, memory, GPU capabilities, and CPU benchmark results.
 *
 * TODO: Implement native device capability detection to properly determine
 * device performance level for native platforms.
 *
 * @returns Promise resolving to device performance level
 */
export async function getDevicePerformanceLevel(): Promise<DevicePerformanceLevel> {
  // React Native's platform-specific file resolution automatically uses web.native.ts on native
  const level = await getPlatformDevicePerformanceLevel();
  return mapToUnifiedLevel(level);
}

// Re-export native constants for backward compatibility (used by VideoPlayer.native.tsx)
// React Native's platform-specific file resolution automatically uses web.native.ts on native
export { DEVICE_PERFORMANCE_LEVEL, DevicePerformanceLevels } from './web';
